// The client's Acknowledgments copy verbatim, one key per paragraph; `InlineMarkdown`
// can't render emphasis, so italics and bold links are flattened.
//
// Left exactly as sent, for the client to resolve:
//   - Azimi bio 3 reads `research funding.Equally committed` — a missing space.
//   - Paula Díaz Levi is listed in both groups with the same bio, so she renders twice.
//   - "Albert Anseh" heads a bio that begins "Albert Oppong-Ansah is…".
//   - Krenak's and Hobbs' bios have no closing full stop.
//   - Hobbs' "while regularly appears as a pundit" lost its subject.
//   - Beiser's Amazon link keeps its search-result tail; `…/dp/0399576428` would do.
// Changed: Goldmark's expiring `urldefense.com` link swapped for the real URL, and the
// "Acknowledgments" heading dropped since `FOOTER_DOCS` titles the card.
//
// The `*Role` keys are ours, condensed from each bio, and still need client sign-off —
// bar Hobbs' and van Spall's, which came with their bios.
export default {
  intro: {
    id: 'rm_footer_contributors_intro',
    defaultMessage:
      'We would like to thank all the contributors who participated in the Mine My Phone project for sharing their invaluable expertise and knowledge to make this project possible.',
  },
  expertsTitle: {
    id: 'rm_footer_contributors_experts_title',
    defaultMessage: 'Expert Contributors Featured in Video Content',
  },
  authorsTitle: {
    id: 'rm_footer_contributors_authors_title',
    defaultMessage: 'Article Authors',
  },

  azimiRole: {
    id: 'rm_footer_contributors_azimi_role',
    defaultMessage: 'Professor, University of Toronto',
  },
  azimiBio1: {
    id: 'rm_footer_contributors_azimi_bio_1',
    defaultMessage:
      'Dr. Gisele Azimi is a Professor at the University of Toronto and the Canada Research Chair in Urban Mining Innovations. Her research is guided by a simple yet powerful principle: the materials needed to build a sustainable future should not come at the planet’s expense.',
  },
  azimiBio2: {
    id: 'rm_footer_contributors_azimi_bio_2',
    defaultMessage:
      'She develops innovative technologies to recover critical minerals from industrial waste, recycle valuable materials from end-of-life products such as batteries, and create cleaner extraction processes that advance the transition to a circular economy. By integrating chemical engineering, materials science, and electrochemistry, her work transforms waste into valuable resources while reducing the environmental impact of resource extraction.',
  },
  azimiBio3: {
    id: 'rm_footer_contributors_azimi_bio_3',
    defaultMessage:
      'Working closely with industry and government partners, Dr. Azimi translates scientific discoveries into practical, real-world solutions. She has published more than 120 scientific papers, holds multiple patents, and has secured over $30 million in research funding.Equally committed to education and mentorship, Dr. Azimi is passionate about preparing the next generation of engineers and scientists to lead the transition toward a cleaner, more resilient, and more sustainable world.',
  },

  beiserRole: {
    id: 'rm_footer_contributors_beiser_role',
    defaultMessage: 'Journalist and author',
  },
  beiserBio1: {
    id: 'rm_footer_contributors_beiser_bio_1',
    defaultMessage:
      'Vince Beiser is an award-winning journalist and bestselling author. He has reported from over 100 countries, states, provinces, kingdoms, occupied territories, no man’s lands and disaster zones. His latest book, “[Power Metal: The Race for the Resources That Will Shape the Future](https://www.penguinrandomhouse.com/books/709947/power-metal-by-vince-beiser/)” is a globe-trotting investigation into how the materials we need for digital technology and renewable energy are causing environmental havoc, political upheaval, mayhem and murder—and how we can do better. The book has been featured on The Daily Show as well as in the New Yorker, the Wall Street Journal, Washington Post and many other outlets. It won Canada’s Balsillie Prize for Public Policy and was a finalist for a New York Public Library award and Europe’s Fetisov Journalism Award for Excellence in Environmental Journalism.',
  },
  beiserBio2: {
    id: 'rm_footer_contributors_beiser_bio_2',
    defaultMessage:
      'Vince’s first book, “[The World in a Grain: The Story of Sand and How It Transformed Civilization](https://www.amazon.com/World-Grain-Story-Transformed-Civilization/dp/0399576428/ref=sr_1_1?s=books&ie=UTF8&qid=1517506372&sr=1-1&refinements=p_27%25253AVince+Beiser),” about the deadly and destructive impacts of humanity’s use of this little-considered yet crucially important natural resource, was a finalist for a PEN America award and a California Book Award, and spawned a TEDx talk. Both books have been translated into many languages.',
  },
  beiserBio3: {
    id: 'rm_footer_contributors_beiser_bio_3',
    defaultMessage:
      'Vince has also exposed conditions in California’s harshest prisons, trained with US Army soldiers, ridden with the first responders to natural disasters, and hunted down other stories from around the world for publications including Wired, The Atlantic, The Guardian, Mother Jones, Rolling Stone, The Economist, The Los Angeles Times, Bloomberg Business Week, The Wall Street Journal, and The New York Times.',
  },
  beiserBio4: {
    id: 'rm_footer_contributors_beiser_bio_4',
    defaultMessage:
      'Vince’s work has been honored by Investigative Reporters and Editors, the Society of Professional Journalists, the Columbia, Medill and Missouri Graduate Schools of Journalism, and many other institutions. He has also shared in three National Magazine Awards and an Emmy.',
  },
  beiserBio5: {
    id: 'rm_footer_contributors_beiser_bio_5',
    defaultMessage:
      'Keep up with Vince’s work via his newsletter, Power Metal, at [https://powermetal.substack.com/](https://powermetal.substack.com/).',
  },

  diazLeviRole: {
    id: 'rm_footer_contributors_diaz_levi_role',
    defaultMessage: 'Science and environmental journalist',
  },
  diazLeviBio: {
    id: 'rm_footer_contributors_diaz_levi_bio',
    defaultMessage:
      'Paula Díaz Levi is a science and environmental journalist with experience in media outlets, conservation organizations, research centers, and projects focused on biodiversity and climate change. She holds a master’s degree in History of Science and Science Communication from the Universitat de València, the Universitat d’Alacant, and the Universidad Miguel Hernández de Elche, and a postgraduate diploma in Science Communication from the University of Chile. She is also a solutions journalism trainer accredited by the Solutions Journalism Network. She is currently the Editor-in-chief and a mentor at Climate Tracker Latin America, where she leads training processes for journalists across the region.',
  },

  goldmarkRole: {
    id: 'rm_footer_contributors_goldmark_role',
    defaultMessage: 'Professor, Columbia Climate School',
  },
  goldmarkBio: {
    id: 'rm_footer_contributors_goldmark_bio',
    defaultMessage:
      'Sandra Goldmark is a Professor of Professional Practice and Associate Dean for Engagement and Impact at the Columbia Climate School. Her work focuses on circular economy, sustainable design, systems thinking, and change leadership. Prior to the Climate School, Sandra was a professor and the inaugural Director of Campus Sustainability at Barnard College. Originally a theatrical set designer, Sandra designed scenery and costumes for theatres around the U.S. and is a co-creator of the [Sustainable Production Toolkit](https://www.sustainableproductiontoolkit.com/). From 2013–2019, Sandra founded and operated Fixup, a social enterprise repair service. Sandra’s work has been featured in The New York Times, The Wall Street Journal, the BBC, The Sunday Times of London and many more. A graduate of Harvard College and Yale University, Sandra is the author of Fixation: How to Have Stuff without Breaking the Planet.',
  },

  maendaKithokoRole: {
    id: 'rm_footer_contributors_maenda_kithoko_role',
    defaultMessage: 'Environmental activist, Génération Lumière',
  },
  maendaKithokoBio1: {
    id: 'rm_footer_contributors_maenda_kithoko_bio_1',
    defaultMessage:
      'David Maenda Kithoko is an environmental activist with a background in human sciences from a preparatory class at the ENS de Lyon (France), followed by studies in international relations at École 3A and Sciences Po Grenoble (France) where he specialized in social and solidarity economy (SSE).',
  },
  maendaKithokoBio2: {
    id: 'rm_footer_contributors_maenda_kithoko_bio_2',
    defaultMessage:
      'He has developed recognized expertise in environmental justice and extractivism.',
  },
  maendaKithokoBio3: {
    id: 'rm_footer_contributors_maenda_kithoko_bio_3',
    defaultMessage:
      'Originally from the mining regions of Kivu, he is now regarded as a specialist in mining issues in the Democratic Republic of Congo (DRC), particularly around minerals such as cobalt, coltan, and copper.',
  },
  maendaKithokoBio4: {
    id: 'rm_footer_contributors_maenda_kithoko_bio_4',
    defaultMessage:
      'These resources, which are essential to digital technologies and the energy transition, lie at the heart of major issues related to armed conflicts, human exploitation conditions, and environmental impacts in eastern DRC.',
  },
  maendaKithokoBio5: {
    id: 'rm_footer_contributors_maenda_kithoko_bio_5',
    defaultMessage:
      'As the founder of the environmental NGO Génération Lumière, David Maenda Kithoko works to raise awareness of the consequences of extractivism and to promote a more just ecological transition.',
  },
  maendaKithokoBio6: {
    id: 'rm_footer_contributors_maenda_kithoko_bio_6',
    defaultMessage:
      'Now a refugee in France, he works as a lecturer (at the University of Lyon II and the Institute for Ecological Transition - Ecologica) and as a speaker on the connections between natural resources, digital technology, the energy transition, and geopolitical challenges.',
  },

  krenakRole: {
    id: 'rm_footer_contributors_krenak_role',
    defaultMessage: 'Indigenous activist and legal anthropologist',
  },
  krenakBio: {
    id: 'rm_footer_contributors_krenak_bio',
    defaultMessage:
      'Edson Krenak is an Indigenous activist, writer, and legal anthropologist dedicated to advancing the rights of Indigenous Peoples in global environmental governance. Through his work with Cultural Survival, he supports Indigenous communities across Brazil and other countries on issues including land rights, climate justice, responsible mining, and community-led advocacy. His work bridges grassroots leadership with international policy processes, engaging governments, investors, companies, and multilateral institutions to strengthen Indigenous rights and accountability. He is the author of "The Borum\'s Dream," recipient of Brazil\'s National Tamoios Award for Indigenous Writers, among other books, and has published several articles on Indigenous culture and legal thought. Edson serves on international governance on Mining tailings and Academic bodies and regularly lectures on Indigenous rights, sustainable development, and legal anthropology',
  },

  maslinRole: {
    id: 'rm_footer_contributors_maslin_role',
    defaultMessage: 'Professor, University College London',
  },
  maslinBio: {
    id: 'rm_footer_contributors_maslin_bio',
    defaultMessage:
      'Mark Maslin BSc, PhD, FRGS, FRSA Professor of Earth System Science at University College London and the Lead on Climate, Health and Security at United Nations University. He is a leading scientist with particular interest in understanding the impacts, politics, and solutions to climate change. Professor Maslin has published over 225 papers in journals such as Nature, Science, and The Lancet. He has received research, consultancy and training funding worth over £85m from government, charities, NGOs and the private sector. Professor Maslin has written 10 books and over 100 popular articles and regularly appears on radio and television, including BBC One David Attenborough’s ‘Climate Change: the facts’. His books include ‘Climate Change: A Very Short Introduction: 4th edition’ (2021), ‘The Human Planet’ with Simon Lewis (2018) ‘How to save our planet: the facts’ (2021) and ‘Climate change: science, politics and solutions’ (2026). He was included in Who’s Who for the first time in 2009, made a Royal Society Industrial Fellow in 2012 and was granted a Royal Society Wolfson Research Merit Scholarship in 2011. He is also the UNFCCC designated point of contact and COP meeting lead for UCL. In 2023 Onalytical listed Professor Maslin as Number 1 Global Sustainability thought leader and influencer of the year. In 2024 he was the Special Advisor to the UK Parliament Environment Audits Committee. He is also a founding member and science expert on Sir David King FRS led international ‘Climate Crisis Advisory Group’.',
  },

  ngangRole: {
    id: 'rm_footer_contributors_ngang_role',
    defaultMessage: 'Environment and climate governance expert',
  },
  ngangBio1: {
    id: 'rm_footer_contributors_ngang_bio_1',
    defaultMessage:
      'Eric Ngang is an Environment and Climate Governance Expert with over 15 years of experience in ESG research, natural resource governance, and policy engagement. He has worked and consulted for organisations including Global Witness, the Natural Resource Governance Institute, and civil society networks across Africa advocating responsible mineral production.',
  },
  ngangBio2: {
    id: 'rm_footer_contributors_ngang_bio_2',
    defaultMessage:
      'He currently works for African Resources Watch (Afrewatch) and leads initiatives that promote environmental justice, community rights, and corporate accountability in the DRC’s natural resource sector. He advances rights-based approaches that strengthen community participation, accountability, and access to justice in environmental decision-making, emphasizing the integration of local knowledge into policy frameworks. He equally serves on the Steering Committee of the African Coalition on Green Minerals (ACGM), a continent-wide coalition advancing accountable, and evidence-based natural resource governance through civil society participation.',
  },

  perrinRole: {
    id: 'rm_footer_contributors_perrin_role',
    defaultMessage: 'Chemist and entrepreneur, REEcover',
  },
  perrinBio1: {
    id: 'rm_footer_contributors_perrin_bio_1',
    defaultMessage:
      'Marie Perrin is a chemist and entrepreneur passionate about sustainable innovation. After studying chemistry at Université Paris-Saclay and ENS Paris-Saclay, she earned a Master’s degree from École Polytechnique before pursuing a PhD at ETH Zurich, where she graduated in 2024 with research focused on bio-inspired rare earth chemistry. Her scientific and entrepreneurial achievements have been recognized with multiple distinctions, including the European Young Chemists Award, the ETH Spark Award, and the EPO Young Inventor Prize.',
  },
  perrinBio2: {
    id: 'rm_footer_contributors_perrin_bio_2',
    defaultMessage:
      'Marie now leads REEcover, a startup project developing an innovative chemical process for the sustainable recycling of rare earth elements, and is currently an ETH Pioneer Fellow. Deeply committed to the societal impact of science, she has been actively involved in mentoring, outreach and science policy through the Young Swiss Chemical Society and the European Young Chemists’ Network, and today she shares her passion for science and the environment on social media as @theraredoctor.',
  },

  smithRole: {
    id: 'rm_footer_contributors_smith_role',
    defaultMessage: 'Journalist, United States',
  },
  smithBio: {
    id: 'rm_footer_contributors_smith_bio',
    defaultMessage:
      '**Noel Lyn Smith** is a journalist based in the United States, where she writes about Native nations. Her writing centers on tribal sovereignty, tribal government, environmental issues such the Gold King Mine spill or coal mining, and fun stuff like fairtime on Navajo land. Noel was a staff reporter with the Farmington Daily Times in northwest New Mexico and with the Navajo Times in Window Rock, Arizona. She is Diné (Navajo).',
  },

  geallRole: {
    id: 'rm_footer_contributors_geall_role',
    defaultMessage: 'Associate Fellow, Chatham House',
  },
  geallBio: {
    id: 'rm_footer_contributors_geall_bio',
    defaultMessage:
      '**Dr Sam Geall** is an Associate Fellow at Chatham House, Honorary Researcher at the Lancaster University China Centre, and a Senior Visiting Research Fellow at the Oxford Institute for Energy Studies. His work focuses on the intersection of energy, climate, and geopolitics with a special focus on China. He previously served as CEO of Dialogue Earth (previously known as China Dialogue) and has written widely on China and clean energy for leading international publications. He was previously Departmental Lecturer in Human Geography of China at University of Oxford. He holds a Ph.D. in Social Anthropology from the University of Manchester.',
  },

  larsonRole: {
    id: 'rm_footer_contributors_larson_role',
    defaultMessage: 'Multiformat journalist, Berlin',
  },
  larsonBio: {
    id: 'rm_footer_contributors_larson_bio',
    defaultMessage:
      '**Caleb Larson** is an American multiformat journalist based in Berlin, Germany. His work covers the intersection of conflict and society with a focus on American foreign policy and European security. Caleb has reported from Germany, Ukraine, Russia, and the United States. Most recently, he covered the war in Ukraine from the ground, reporting extensively on the war’s shifting battle lines from Bakhmut and other cities in Donbas and writing on the war’s civilian and humanitarian toll.',
  },

  mendozaRole: {
    id: 'rm_footer_contributors_mendoza_role',
    defaultMessage: 'Investigative journalist, Bolivia',
  },
  mendozaBio: {
    id: 'rm_footer_contributors_mendoza_bio',
    defaultMessage:
      '**Sergio Mendoza** is an investigative journalist based in Bolivia, specializing in economics and sustainable development. With more than 10 years of experience, he has written for local and international media. He is passionate about stories that shed light on the movement of money and explore how economic development can go hand in hand with environmental protection. He holds an MA in Investigative Reporting from Birkbeck, University of London, which he completed as a Chevening Scholar.',
  },

  daviesRole: {
    id: 'rm_footer_contributors_davies_role',
    defaultMessage: 'English-French culture writer',
  },
  daviesBio: {
    id: 'rm_footer_contributors_davies_bio',
    defaultMessage:
      'The English-French culture writer Hannah JD Davies is a freelance journalist with bylines at the likes of Vogue, Time Out, VICE and many others. She was the Guardian’s deputy TV editor between 2019 and 2021, while she headed up this UK paper’s newsletters between 2021 and 2024. Her work is driven by showing how pop culture permeates through society and politics, and Davies has an MA in arts journalism at New York City’s Columbia University.',
  },

  oppongAnsahRole: {
    id: 'rm_footer_contributors_oppong_ansah_role',
    defaultMessage: 'Science journalist, Ghana News Agency',
  },
  oppongAnsahBio: {
    id: 'rm_footer_contributors_oppong_ansah_bio',
    defaultMessage:
      'Albert Oppong-Ansah is a field-leading Ghanaian science and environmental journalist. He heads up the environment, science, energy and agriculture desk at the Ghana News Agency, while he won the esteemed 2025 Eric and Wendy Schmidt Science Communication Award for his impactful reporting on air pollution. A lot of his work is driven by exposing how Africa’s natural landscape and every day people tend to pay an enormous price (and are exposed to big health risks) for extractivism, analysing whether this is ultimately worth it.',
  },

  hobbsRole: {
    id: 'rm_footer_contributors_hobbs_role',
    defaultMessage: 'Freelance journalist, UK',
  },
  hobbsBio: {
    id: 'rm_footer_contributors_hobbs_bio',
    defaultMessage:
      'Thomas Hobbs is a freelance culture and technology journalist, based in the UK. His work has appeared in titles including the Guardian, Financial Times, Empire Magazine, BBC Culture, Dazed, Wired, Telegraph, New York Times and many others. He has interviewed legendary figures such as John Carpenter, Nas, and Usher, while regularly appears as a pundit on BBC radio. Hobbs also co-leads the editorial at Back Market',
  },

  vanSpallRole: {
    id: 'rm_footer_contributors_van_spall_role',
    defaultMessage: 'Editor and strategist, UK',
  },
  vanSpallBio1: {
    id: 'rm_footer_contributors_van_spall_bio_1',
    defaultMessage:
      'India Blue van Spall is an editor, writer, and content strategist, currently working as Global Editorial Lead at Back Market, with over a decade of experience spanning fashion, technology and culture. She has held senior editorial and creative roles at WeTransfer, THE FACE and Bottega Veneta, and has contributed to publications including Dazed, Crack Magazine, and It’s Nice That.',
  },
  vanSpallBio2: {
    id: 'rm_footer_contributors_van_spall_bio_2',
    defaultMessage:
      'As a writer and interviewer, she has interviewed figures including FKA twigs, Michaela Coel, Kirsten Dunst and Sofia Coppola, covering fashion, music, film and culture. Alongside her editorial career, India worked as a voiceover artist for more than 20 years, with credits including the BBC, Channel 4, Sky and Disney.',
  },
} as const
